import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { ApiStatus, RootState } from '~/store/types/sharedTypes';
import { InvoiceTabValue } from '~/Interfaces';
import useTranslate from '~/hooks/useTranslate';
import { theme } from '~/styles/themes';
import { useHistory } from 'react-router';
import { getDebtorInvoices } from '~/store/actions/invoiceActions';
import { Button, ButtonType, Stack, useWindowProperties } from '@qred/qit-ui';
import SpinnerCircularSmall from '~/components/shared/Spinner/SpinnerCircularSmall';
import SpinnerCircular from '~/components/shared/Spinner/SpinnerCircular';
import {
  FontWeights,
  Typography,
} from '~/components/shared/Typography/Typography';
import {
  InvoiceLoaderHolder,
  PaymentMethodMainContainer,
  PaymentMethodPageEnum,
} from './Invoice';
import ListOfInvoicesSubMenu from './components/ListOfInvoices/ListOfInvoicesSubMenu';
import ListOfInvoiceSingleItem, {
  SingleItemContainer,
} from './components/ListOfInvoices/ListOfInvoiceSingleItem';
import InvoicePageHeader from './components/InvoicePageHeader';
import NotConnected from './components/NotConnected';
import CustomerSupport from './components/CustomerSupport';

export const ListBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: left;
  align-self: center;
  flex: 1 0;
  width: 100%;
  background-color: #f8f9f5;
  @media (orientation: landscape) {
    max-width: 40em;
    padding: 0 2em;
  }
  margin-top: 5em;
  margin-bottom: 2em;
`;
const InvoicesLoaderHolder = styled.div`
  height: 40px;
  width: 40px;
  margin-top: 15px;
`;

const ListOfInvoices = () => {
  const [selectedSection, setSelectedSection] = useState<InvoiceTabValue>(
    InvoiceTabValue.OPEN
  );
  const [paginationPage, setPaginationPage] = useState<number>(1);
  const [loadingEnabled, setLoadingEnabled] = useState<boolean>(true);
  const {
    singleInvoice,
    debtorInvoices,
    debtorInvoicesApiStatus,
  } = useSelector((state: RootState) => state.factoring);
  const translate = useTranslate();
  const history = useHistory();
  const dispatch = useDispatch();
  const { isDesktop } = useWindowProperties();

  const invoicesEndRef = useRef<null | HTMLDivElement>(null);

  const [paymentMethodPage, setPaymentMethodPage] = useState<number>(
    PaymentMethodPageEnum.None
  );

  const openCustomerSupportPage = () => {
    history.push({
      pathname: '/customer-support',
    });
  };

  const handleSubMenuClick = (value: InvoiceTabValue) => {
    setLoadingEnabled(true);
    setPaginationPage(1);
    dispatch(
      getDebtorInvoices(
        singleInvoice.debtorOrgNumber ??
          singleInvoice.debtor.identificationNumber,
        1,
        10,
        value
      )
    );
    setSelectedSection(value);
  };

  const handleInvoiceClick = (id: string) => {
    history.push({
      pathname: `/invoices/${id}`,
    });
  };

  const onNext = () => {
    setLoadingEnabled(false);
    dispatch(
      getDebtorInvoices(
        singleInvoice.debtorOrgNumber ??
          singleInvoice.debtor.identificationNumber,
        paginationPage + 1,
        10,
        selectedSection,
        true
      )
    );
    setPaginationPage(paginationPage + 1);
  };

  const onBackButtonClick = () => {
    dispatch(
      getDebtorInvoices(
        singleInvoice.debtorOrgNumber ??
          singleInvoice.debtor.identificationNumber,
        1,
        10,
        InvoiceTabValue.OPEN
      )
    );
    history.push({
      pathname: '/invoices',
    });
  };

  const renderContent = () => {
    if (Object.keys(singleInvoice).length === 0) {
      return <NotConnected onBackButtonClick={onBackButtonClick} />;
    }
    if (paymentMethodPage === PaymentMethodPageEnum.Support) {
      return (
        <CustomerSupport
          onCancelButton={() =>
            setPaymentMethodPage(PaymentMethodPageEnum.None)
          }
        />
      );
    }
    return (
      <PaymentMethodMainContainer>
        <InvoicePageHeader
          languagePicker
          backButtonLabel="Invoices.Invoices"
          onBackButton={onBackButtonClick}
          customerService={isDesktop}
          onCustomerService={openCustomerSupportPage}
          highlightedSection="myInvoices"
        />
        <ListBodyContainer>
          <ListOfInvoicesSubMenu
            onItem={handleSubMenuClick}
            selectedItem={selectedSection}
          />
          <Typography
            fontSize={1.25}
            fontWeight={FontWeights.Thin}
            color={theme.colors.dark}
            style={{
              lineHeight: 1.5,
              marginBottom: '1em',
            }}
          >
            {`${translate('Invoices.Hello', {
              client: singleInvoice?.debtor?.name,
            })}`}
          </Typography>
          {debtorInvoicesApiStatus === ApiStatus.Started && loadingEnabled ? (
            <InvoiceLoaderHolder>
              <SpinnerCircular color={theme.colors.secondary} />
            </InvoiceLoaderHolder>
          ) : (
            <>
              {debtorInvoices.length > 0 ? (
                debtorInvoices.map((invoice, i) => (
                  <ListOfInvoiceSingleItem
                    key={`${invoice.id}`}
                    invoiceData={invoice}
                    onClickHandler={() =>
                      handleInvoiceClick(
                        invoice.externalId
                          ? invoice.externalId
                          : btoa(invoice.id)
                      )
                    }
                  />
                ))
              ) : (
                <SingleItemContainer>
                  {translate('Invoices.NoInvoices')}
                </SingleItemContainer>
              )}
              {debtorInvoices.length > 0 && (
                <Stack align="center">
                  {debtorInvoicesApiStatus === ApiStatus.Started ? (
                    <InvoicesLoaderHolder>
                      <SpinnerCircularSmall />
                    </InvoicesLoaderHolder>
                  ) : (
                    <>
                      <Button
                        type={ButtonType.NoBorder}
                        onClick={onNext}
                        m="auto 0"
                        color={theme.colors.accent}
                        icon="ChevronDown"
                        iconRight
                      >
                        {translate('ShowMore')}
                      </Button>
                    </>
                  )}
                </Stack>
              )}
              <div ref={invoicesEndRef} />
            </>
          )}
        </ListBodyContainer>
      </PaymentMethodMainContainer>
    );
  };

  return renderContent();
};

export default ListOfInvoices;
